export type WorkplaceRequestCommentUser = {
  default_organization_id: string;
  email: string;
  id: string;
  name: string;
  phone: string;
  profile_photo_url: string;
  roles: string[];
  title: string;
};

/**
 * A file attached to a comment, as read back from the API. The `url` is a
 * short-lived CloudFront-signed link; the underlying object path is never
 * returned, so re-submitting an existing attachment means deriving the path
 * from this URL (see `attachmentUrlToPath` in `@gettactic/helpers`).
 */
export type WorkplaceRequestCommentAttachment = {
  url: string;
  filename?: string;
};

/**
 * A file submitted with a comment: the object path returned by the ticket
 * uploads endpoint once the bytes have been PUT to its signed URL.
 */
export type WorkplaceRequestCommentAttachmentInput = {
  path: string;
  filename?: string;
};

/** Most attachments a single comment may carry (enforced by the backend). */
export const MAX_COMMENT_ATTACHMENTS = 20;

export type WorkplaceRequestComment = {
  attachments: WorkplaceRequestCommentAttachment[];
  comment: string;
  created: string;
  id: string;
  /** Visible only to managers of the request's type; never shown to the requester. */
  internal: boolean;
  updated: string;
  user: WorkplaceRequestCommentUser;
};

export type WorkplaceRequestCommentsResponse = {
  cursor: string | null;
  elements: WorkplaceRequestComment[];
};

export type WorkplaceRequestCommentsParams = {
  cursor?: string;
  limit?: number;
  tz?: string;
};

/** A comment needs text, at least one attachment, or both. */
export type CreateWorkplaceRequestCommentParams = {
  attachments?: WorkplaceRequestCommentAttachmentInput[];
  comment?: string;
  /** Post as an internal note. Requires `capabilities.can_manage` on the request. */
  internal?: boolean;
  tz?: string;
};

/**
 * Omitting `attachments` keeps the ones the comment already has; passing it
 * replaces the whole list. The result must still have text or an attachment.
 */
export type UpdateWorkplaceRequestCommentParams = {
  attachments?: WorkplaceRequestCommentAttachmentInput[];
  comment?: string;
  tz?: string;
};
